import React, { useState, useEffect, useCallback } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';
import type { CustomPricing, PricingEntry } from '@lds-efsp/shared-contracts';
import {
  SetupDetailDrawer,
  SetupFilterBar,
  SetupDataTable,
  StatusBadge,
  SetupItemPreviewPopover,
} from '../components';
import FormInput, { FormSelect, FormTextarea, FormCheckbox } from '../components/FormInput';
import { useApi } from '../../../api/useApi';

interface SetupCustomPricingProps {
  brand: BrandConfig;
  Icons: IconsType;
}

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'expired', label: 'Expired' },
  { value: 'scheduled', label: 'Scheduled' },
];

const ruleTypeOptions = [
  { value: 'discount-percent', label: 'Discount (%)' },
  { value: 'discount-flat', label: 'Discount ($)' },
  { value: 'override-price', label: 'Override Price' },
  { value: 'markup-percent', label: 'Markup (%)' },
  { value: 'markup-flat', label: 'Markup ($)' },
];

const SetupCustomPricing: React.FC<SetupCustomPricingProps> = ({ brand, Icons }) => {
  const { get, post, patch } = useApi();
  const [pricingRules, setPricingRules] = useState<CustomPricing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPricing, setSelectedPricing] = useState<CustomPricing | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<Partial<CustomPricing>>({});
  const [isSaving, setIsSaving] = useState(false);

  const fetchPricingRules = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter !== 'all') params.set('status', statusFilter);

      const data = await get<{ data: CustomPricing[] }>(`/api/admin/setup/custom-pricing?${params}`);
      setPricingRules(data.data || []);
    } catch (error) {
      console.error('Failed to fetch custom pricing:', error);
    } finally {
      setIsLoading(false);
    }
  }, [get, search, statusFilter]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
    fetchPricingRules();
  }, [fetchPricingRules]);

  const handleRowClick = (pricing: CustomPricing) => {
    setSelectedPricing(pricing);
    setFormData(pricing);
    setIsCreating(false);
    setIsDrawerOpen(true);
  };

  const handleAdd = () => {
    setSelectedPricing(null);
    setFormData({
      name: '',
      accountId: '',
      accountName: '',
      entries: [],
      effectiveDate: new Date().toISOString(),
      priority: 0,
      applyToAllProducts: false,
      stackable: false,
      status: 'active',
    });
    setIsCreating(true);
    setIsDrawerOpen(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: parseInt(value, 10) || 0 }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleEntryChange = (index: number, field: keyof PricingEntry, value: unknown) => {
    setFormData(prev => {
      const entries = [...(prev.entries || [])];
      entries[index] = { ...entries[index], [field]: value };
      return { ...prev, entries };
    });
  };

  const handleAddEntry = () => {
    setFormData(prev => ({
      ...prev,
      entries: [
        ...(prev.entries || []),
        {
          id: crypto.randomUUID(),
          ruleType: 'discount-percent' as const,
          value: 0,
        },
      ],
    }));
  };

  const handleRemoveEntry = (index: number) => {
    setFormData(prev => ({
      ...prev,
      entries: prev.entries?.filter((_, i) => i !== index) || [],
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (isCreating) {
        await post('/api/admin/setup/custom-pricing', formData);
      } else {
        await patch(`/api/admin/setup/custom-pricing/${selectedPricing?.id}`, formData);
      }

      setIsDrawerOpen(false);
      fetchPricingRules();
    } catch (error) {
      console.error('Failed to save custom pricing:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (date: string | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (pricing: CustomPricing) => (
        <div className="flex items-center gap-2">
          <div>
            <div className="font-medium" style={{ color: brand.colors.link }}>
              {pricing.name}
            </div>
            {pricing.description && (
              <div className="text-xs" style={{ color: brand.colors.textLight }}>
                {pricing.description}
              </div>
            )}
          </div>
          <SetupItemPreviewPopover
            brand={brand}
            title={pricing.name}
            fields={[
              { label: 'Account', value: pricing.accountName },
              { label: 'Account #', value: pricing.accountNumber || '-' },
              { label: 'Effective', value: formatDate(pricing.effectiveDate) },
              { label: 'Expires', value: pricing.expirationDate ? formatDate(pricing.expirationDate) : 'Never' },
              { label: 'Priority', value: String(pricing.priority) },
            ]}
          />
        </div>
      ),
    },
    {
      key: 'account',
      header: 'Account',
      render: (pricing: CustomPricing) => (
        <div>
          <div className="font-medium" style={{ color: brand.colors.text }}>
            {pricing.accountName}
          </div>
          <div className="text-xs" style={{ color: brand.colors.textLight }}>
            {pricing.accountNumber}
          </div>
        </div>
      ),
    },
    {
      key: 'entries',
      header: 'Rules',
      render: (pricing: CustomPricing) => (
        <span>{pricing.entries.length} {pricing.entries.length === 1 ? 'rule' : 'rules'}</span>
      ),
    },
    {
      key: 'effectiveDate',
      header: 'Effective',
      render: (pricing: CustomPricing) => (
        <span>{formatDate(pricing.effectiveDate)}</span>
      ),
    },
    {
      key: 'expirationDate',
      header: 'Expires',
      render: (pricing: CustomPricing) => (
        <span>{pricing.expirationDate ? formatDate(pricing.expirationDate) : 'Never'}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (pricing: CustomPricing) => <StatusBadge status={pricing.status} />,
    },
  ];

  return (
    <div>
      <SetupFilterBar
        brand={brand}
        Icons={Icons}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search custom pricing..."
        statusFilter={statusFilter}
        statusOptions={statusOptions}
        onStatusChange={setStatusFilter}
        onAdd={handleAdd}
        addLabel="Add Pricing Rule"
      />

      <SetupDataTable
        brand={brand}
        Icons={Icons}
        columns={columns}
        data={pricingRules}
        isLoading={isLoading}
        onRowClick={handleRowClick}
        emptyMessage="No custom pricing rules found"
        getRowId={(pricing) => pricing.id}
      />

      <SetupDetailDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title={isCreating ? 'Add Pricing Rule' : 'Edit Pricing Rule'}
        subtitle={selectedPricing?.name}
        brand={brand}
        Icons={Icons}
        width="lg"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsDrawerOpen(false)}
              className="px-4 py-2 border rounded-lg text-sm font-medium transition-colors hover:bg-gray-50"
              style={{ borderColor: brand.colors.border, color: brand.colors.text }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        }
      >
        <div className="space-y-6">
          <FormInput
            brand={brand}
            label="Name"
            name="name"
            value={formData.name || ''}
            onChange={handleInputChange}
            required
            placeholder="e.g., Anderson Law Firm - Volume Discount"
          />

          <FormTextarea
            brand={brand}
            label="Description"
            name="description"
            value={formData.description || ''}
            onChange={handleInputChange}
            rows={2}
          />

          <div className="grid grid-cols-2 gap-4">
            <FormInput
              brand={brand}
              label="Account Name"
              name="accountName"
              value={formData.accountName || ''}
              onChange={handleInputChange}
              required
              placeholder="Account name"
            />
            <FormInput
              brand={brand}
              label="Account Number"
              name="accountNumber"
              value={formData.accountNumber || ''}
              onChange={handleInputChange}
              placeholder="ACC-001"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormInput
              brand={brand}
              label="Effective Date"
              name="effectiveDate"
              type="date"
              value={formData.effectiveDate?.split('T')[0] || ''}
              onChange={handleInputChange}
              required
            />
            <FormInput
              brand={brand}
              label="Expiration Date"
              name="expirationDate"
              type="date"
              value={formData.expirationDate?.split('T')[0] || ''}
              onChange={handleInputChange}
              helpText="Leave empty for no expiration"
            />
          </div>

          <div
            className="border-t pt-4"
            style={{ borderColor: brand.colors.border }}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium" style={{ color: brand.colors.text }}>
                Pricing Rules
              </h3>
              <button
                onClick={handleAddEntry}
                className="flex items-center gap-1 text-sm font-medium"
                style={{ color: brand.colors.link }}
              >
                <Icons.Plus className="w-4 h-4" />
                Add Rule
              </button>
            </div>

            {formData.entries?.length === 0 ? (
              <div
                className="text-center py-6 border rounded-lg"
                style={{ borderColor: brand.colors.border, color: brand.colors.textLight }}
              >
                No pricing rules. Click "Add Rule" to create one.
              </div>
            ) : (
              <div className="space-y-3">
                {formData.entries?.map((entry, index) => (
                  <div
                    key={entry.id}
                    className="p-4 border rounded-lg"
                    style={{ borderColor: brand.colors.border }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-sm font-medium" style={{ color: brand.colors.text }}>
                        Rule {index + 1}
                      </span>
                      <button
                        onClick={() => handleRemoveEntry(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Icons.X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label htmlFor={`pricing-rule-type-${index}`} className="block text-xs mb-1" style={{ color: brand.colors.textLight }}>Rule Type</label>
                        <select
                          id={`pricing-rule-type-${index}`}
                          value={entry.ruleType}
                          onChange={(e) => handleEntryChange(index, 'ruleType', e.target.value)}
                          className="w-full px-2 py-1.5 border rounded text-sm"
                          style={{ borderColor: brand.colors.border }}
                        >
                          {ruleTypeOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor={`pricing-value-${index}`} className="block text-xs mb-1" style={{ color: brand.colors.textLight }}>Value</label>
                        <input
                          id={`pricing-value-${index}`}
                          type="number"
                          value={entry.value}
                          onChange={(e) => handleEntryChange(index, 'value', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1.5 border rounded text-sm"
                          style={{ borderColor: brand.colors.border }}
                        />
                      </div>
                      <div>
                        <label htmlFor={`pricing-product-${index}`} className="block text-xs mb-1" style={{ color: brand.colors.textLight }}>Product/Category</label>
                        <input
                          id={`pricing-product-${index}`}
                          type="text"
                          value={entry.productCategory || entry.productName || ''}
                          onChange={(e) => handleEntryChange(index, 'productCategory', e.target.value)}
                          className="w-full px-2 py-1.5 border rounded text-sm"
                          style={{ borderColor: brand.colors.border }}
                          placeholder="All products"
                        />
                      </div>
                    </div>
                    <div className="mt-3">
                      <label htmlFor={`pricing-notes-${index}`} className="block text-xs mb-1" style={{ color: brand.colors.textLight }}>Notes</label>
                      <input
                        id={`pricing-notes-${index}`}
                        type="text"
                        value={entry.notes || ''}
                        onChange={(e) => handleEntryChange(index, 'notes', e.target.value)}
                        className="w-full px-2 py-1.5 border rounded text-sm"
                        style={{ borderColor: brand.colors.border }}
                        placeholder="Optional notes"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div
            className="border-t pt-4"
            style={{ borderColor: brand.colors.border }}
          >
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                brand={brand}
                label="Priority"
                name="priority"
                type="number"
                value={formData.priority || 0}
                onChange={handleInputChange}
                helpText="Higher priority takes precedence"
              />
              <FormSelect
                brand={brand}
                label="Status"
                name="status"
                value={formData.status || 'active'}
                onChange={handleInputChange}
                options={statusOptions}
              />
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <FormCheckbox
                brand={brand}
                label="Apply to All Products"
                name="applyToAllProducts"
                checked={formData.applyToAllProducts || false}
                onChange={handleInputChange}
              />
              <FormCheckbox
                brand={brand}
                label="Stackable with Other Discounts"
                name="stackable"
                checked={formData.stackable || false}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div
            className="border-t pt-4"
            style={{ borderColor: brand.colors.border }}
          >
            <FormTextarea
              brand={brand}
              label="Notes"
              name="notes"
              value={formData.notes || ''}
              onChange={handleInputChange}
              rows={2}
            />
          </div>
        </div>
      </SetupDetailDrawer>
    </div>
  );
};

export default SetupCustomPricing;
